import AuthService from './AuthService';

class ChatbotService {
    constructor() {
        const rawBaseUrl = process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || '';
        this.apiBaseUrl = rawBaseUrl.replace(/\/+$/, '');
    }

    getAuthHeaders(extraHeaders) {
        const token = AuthService.getAccessToken();
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
        return {
            ...authHeader,
            ...(extraHeaders || {}),
        };
    }

    async getChatbots() {
        const loginAs = AuthService.getLoginAs();
        const loginAsParam = loginAs ? `?loginAs=${loginAs}` : '';
        const url = `${this.apiBaseUrl}/private/chatbots${loginAsParam}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async getAdminChatbots(clientId) {
        if (!clientId) {
            throw new Error('clientId is required');
        }

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/chatbots`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async updateChatbot(chatbotId, payload) {
        if (!chatbotId) {
            throw new Error('chatbotId is required');
        }

        const url = `${this.apiBaseUrl}/private/chatbots/${encodeURIComponent(chatbotId)}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                ...this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            },
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async uploadChatbotImage(chatbotId, file, imageType = 'image') {
        if (!chatbotId) {
            throw new Error('chatbotId is required');
        }
        if (!file) {
            throw new Error('file is required');
        }

        const formData = new FormData();
        formData.append('chatbotId', chatbotId);
        formData.append('imageType', imageType);
        formData.append('file', file);

        const url = `${this.apiBaseUrl}/private/chatbot/upload`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async getAdminChatbot(clientId, chatbotId) {
        if (!clientId) throw new Error('clientId is required');
        if (!chatbotId) throw new Error('chatbotId is required');

        const data = await this.getAdminChatbots(clientId);
        if (!Array.isArray(data)) return null;
        return data.find((c) => String(c.chatbotId) === String(chatbotId)) || null;
    }

    async createAdminChatbot(clientId, payload) {
        if (!clientId) throw new Error('clientId is required');

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/chatbots`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async updateAdminChatbot(clientId, chatbotId, payload) {
        if (!clientId) throw new Error('clientId is required');
        if (!chatbotId) throw new Error('chatbotId is required');

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/chatbots/${encodeURIComponent(chatbotId)}`;
        console.log('Updating admin chatbot with URL:', url);
        const response = await fetch(url, {
            method: 'PUT',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async deleteAdminChatbot(clientId, chatbotId) {
        if (!clientId) throw new Error('clientId is required');
        if (!chatbotId) throw new Error('chatbotId is required');

        const url = `${this.apiBaseUrl}/admin/clients/${encodeURIComponent(clientId)}/chatbots/${encodeURIComponent(chatbotId)}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        try {
            return text ? JSON.parse(text) : { ok: true };
        } catch {
            return { ok: true };
        }
    }

    async getEnabledFeatures(loginAs) {
        const loginAsParam = loginAs ? `?loginAs=${loginAs}` : '';
        const url = `${this.apiBaseUrl}/private/features${loginAsParam}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async getFeatureAccess(loginAs) {
        const loginAsParam = loginAs ? `?loginAs=${loginAs}` : '';
        const url = `${this.apiBaseUrl}/private/features/access${loginAsParam}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data)) {
            return { features: data, permissions: [], upgradeable: false };
        }

        return {
            features: Array.isArray(data?.features) ? data.features : [],
            permissions: Array.isArray(data?.permissions) ? data.permissions : [],
            role: String(data?.role || ''),
            permissionGroupIds: Array.isArray(data?.permissionGroupIds) ? data.permissionGroupIds : [],
            planId: String(data?.planId || ''),
            planName: String(data?.planName || ''),
            upgradeable: Boolean(data?.upgradeable),
        };
    }
}

const chatbotService = new ChatbotService();
export { ChatbotService };
export default chatbotService;
